/**
 * FundraisingCard.jsx — Tarjeta de recaudación vista por el fan.
 * Diseño: dark palette con tokens CSS del post de recaudación.
 * Soporta pago con Stripe y PayPal.
 */
import { useState, useEffect } from "react";
import { Check, Lock, Timer, ChevronDown, CreditCard, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { API } from "../../lib/api";

/* ─── Design tokens ─────────────────────────────────────────────────────────── */
const T = {
  bg: "#0a0a0a",
  card: "#131316",
  card2: "#1b1b1f",
  line: "#2a2a30",
  pink: "#FF0066",
  pinkDim: "rgba(255,0,102,0.12)",
  cyan: "#00E5FF",
  amber: "#FFB800",
  text: "#f5f5f7",
  sub: "#9a9aa5",
  sub2: "#6b6b74",
};

const fmt = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(n);

const ES_WEEKDAYS = ["domingo","lunes","martes","miércoles","jueves","viernes","sábado"];
const ES_MONTHS_FULL = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];

/** Formatea un Date como "lunes, 20 de marzo a las 18:00" */
function fmtUnlockDate(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const day = ES_WEEKDAYS[d.getDay()];
  const date = d.getDate();
  const month = ES_MONTHS_FULL[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day}, ${date} de ${month} de ${year} a las ${hh}:${mm}`;
}

/** Banner visible para fans cuando las aportaciones aún no están abiertas */
function CountdownBanner({ countdown, unlockAt }) {
  if (!countdown) return null;
  const segments = [
    { val: countdown.days,  label: "días" },
    { val: countdown.hours, label: "horas" },
    { val: countdown.mins,  label: "min" },
    { val: countdown.secs,  label: "seg" },
  ];
  return (
    <div
      className="rounded-2xl p-4 space-y-4"
      style={{ background: "rgba(255,0,102,0.07)", border: "1.5px solid rgba(255,0,102,0.3)" }}
      data-testid="fr-countdown-banner"
    >
      {/* Cabecera */}
      <div className="flex items-center gap-2">
        <CalendarClock size={16} style={{ color: "rgba(255,80,130,0.9)", flexShrink: 0 }} />
        <span className="text-sm font-black uppercase tracking-wide" style={{ color: "rgba(255,80,130,0.95)", fontFamily: "'Barlow Condensed', sans-serif" }}>
          Aportaciones aún no disponibles
        </span>
      </div>

      {/* Fecha exacta */}
      <div className="space-y-0.5">
        <p className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "rgba(255,255,255,0.35)" }}>
          Se abrirán el
        </p>
        <p className="text-sm font-bold leading-snug" style={{ color: "#fff" }}>
          {fmtUnlockDate(unlockAt)}
        </p>
      </div>

      {/* Separador */}
      <div style={{ height: 1, background: "rgba(255,0,102,0.2)" }} />

      {/* Título del countdown */}
      <div>
        <p className="text-[11px] uppercase tracking-widest font-bold mb-2" style={{ color: "rgba(255,255,255,0.35)" }}>
          Tiempo restante para poder aportar
        </p>
        {/* Dígitos grandes */}
        <div className="grid grid-cols-4 gap-2">
          {segments.map(({ val, label }) => (
            <div key={label} className="flex flex-col items-center rounded-xl py-2"
              style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,0,102,0.2)" }}>
              <span
                className="font-mono font-black leading-none"
                style={{ color: "#fff", fontSize: 26, letterSpacing: "-0.02em" }}
              >
                {String(val).padStart(2, "0")}
              </span>
              <span className="text-[10px] uppercase tracking-widest mt-1" style={{ color: "rgba(255,255,255,0.35)" }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Nota informativa */}
      <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
        Cuando la cuenta atrás llegue a cero podrás elegir una recompensa y realizar tu aportación.
      </p>
    </div>
  );
}

function timeAgo(iso) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "ahora mismo";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

function Avatar({ name, photoUrl, size = 38 }) {
  const initials = (name || "?").slice(0, 2).toUpperCase();
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className="flex-shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    );
  }
  return (
    <div
      className="flex-shrink-0 rounded-full flex items-center justify-center font-bold"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        background: "linear-gradient(135deg, #FF0066 0%, #9b30ff 100%)",
        color: "#fff",
        fontFamily: "'Barlow Condensed', sans-serif",
      }}
    >
      {initials}
    </div>
  );
}

/* ─── Progress bar — tall, glowing, animated ────────────────────────────────── */
function ProgressBar({ raised, goal, tiers = [] }) {
  const pct = Math.min((raised / Math.max(goal, 1)) * 100, 100);
  return (
    <div className="space-y-1.5">
      <div className="relative w-full" style={{ height: 18, borderRadius: 99 }}>
        {/* Track */}
        <div className="absolute inset-0 rounded-full"
          style={{ background: "#111115", border: `1px solid ${T.line}` }} />
        {/* Animated fill */}
        <div
          className="absolute top-0 left-0 h-full rounded-full"
          style={{
            width: `${pct}%`,
            background: pct >= 100
              ? "linear-gradient(90deg,#FFB800,#FF8C00)"
              : "linear-gradient(90deg,#FF0066 0%,#ff3d94 55%,#FFB800 100%)",
            boxShadow: pct > 0 ? `0 0 12px 2px rgba(255,0,102,0.45), 0 0 4px rgba(255,184,0,0.3)` : "none",
            transition: "width 0.9s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {/* Shimmer sweep */}
          {pct > 5 && (
            <span
              className="absolute inset-0 rounded-full"
              style={{
                background: "linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.18) 50%,transparent 100%)",
                animation: "shimmer 2.4s infinite",
              }}
            />
          )}
        </div>
        {/* Tier markers */}
        {tiers.map((tier) => {
          const mp = Math.min((tier.amount / Math.max(goal, 1)) * 100, 100);
          if (mp <= 0 || mp >= 100) return null;
          return (
            <div key={tier.id}
              className="absolute top-1 bottom-1 w-0.5"
              style={{
                left: `${mp}%`,
                background: raised >= tier.amount ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.18)",
                borderRadius: 2,
              }}
            />
          );
        })}
      </div>
      {/* Shimmer keyframe */}
      <style>{`@keyframes shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(200%)}}`}</style>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────────── */
export default function FundraisingCard({ post: initialPost, creatorName, creatorSlug, creatorAvatar, authUser, paymentMode = "live" }) {
  const [post, setPost] = useState(initialPost);
  const [selectedTierId, setSelectedTierId] = useState("");
  const [freeAmount, setFreeAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [payMethod, setPayMethod] = useState("stripe"); // "stripe" | "paypal"
  const [paypalClientId, setPaypalClientId] = useState("");
  const [justUpdated, setJustUpdated] = useState(false); // pulse on new contribution
  const [feeInfo, setFeeInfo] = useState({ rate: 0.15, pct: 15, is_custom: false, expires_at: null });
  const [feeCountdown, setFeeCountdown] = useState(null);

  // Keep post fresh — re-use initialPost if it updates
  useEffect(() => { setPost(initialPost); }, [initialPost]);

  // Fetch personalised commission for logged-in consumer
  useEffect(() => {
    if (!authUser) return;
    axios.get(`${API}/me/commission`, { withCredentials: true })
      .then(res => setFeeInfo(res.data))
      .catch(() => {});
  }, [authUser]);

  // Live countdown
  useEffect(() => {
    if (!feeInfo.is_custom || !feeInfo.expires_at) { setFeeCountdown(null); return; }
    const calc = () => {
      const ms = new Date(feeInfo.expires_at) - new Date();
      if (ms <= 0) return null;
      const total = Math.floor(ms / 1000);
      const d = Math.floor(total / 86400);
      const h = Math.floor((total % 86400) / 3600);
      const m = Math.floor((total % 3600) / 60);
      return { d, h, m };
    };
    setFeeCountdown(calc());
    const id = setInterval(() => setFeeCountdown(calc()), 60000);
    return () => clearInterval(id);
  }, [feeInfo.is_custom, feeInfo.expires_at]);

  // ── Live countdown ─────────────────────────────────────────────────────────
  const [countdown, setCountdown] = useState(null); // { days, hours, mins, secs } | null
  useEffect(() => {
    if (!post.unlock_at) { setCountdown(null); return; }
    const update = () => {
      const ms = new Date(post.unlock_at).getTime() - Date.now();
      if (ms <= 0) { setCountdown(null); return; }
      const s = Math.floor(ms / 1000);
      setCountdown({
        days:  Math.floor(s / 86400),
        hours: Math.floor((s % 86400) / 3600),
        mins:  Math.floor((s % 3600) / 60),
        secs:  s % 60,
      });
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, [post.unlock_at]);

  const isLocked = countdown !== null; // pagos bloqueados mientras cuenta atrás activa

  // Fetch PayPal client_id from platform config
  useEffect(() => {
    axios.get(`${API}/platform-config/public`)
      .then((r) => { if (r.data?.paypal_client_id) setPaypalClientId(r.data.paypal_client_id); })
      .catch(() => {});
  }, []);

  // ── Real-time polling — updates progress bar without page reload ──────────────
  useEffect(() => {
    const POLL_MS = 12000; // 12 seconds
    let timer;

    const poll = async () => {
      // Skip when tab is hidden to save bandwidth
      if (document.visibilityState === "hidden") return;
      // Stop polling once goal is fully reached (usando real, no visual)
      if (post.raised_amount >= post.goal_amount) return;
      try {
        const res = await axios.get(`${API}/fundraising/posts/${post.id}`);
        const fresh = res.data;
        const freshVisual = (fresh.raised_amount || 0) + (fresh.simulated_raised_amount || 0);
        const currentVisual = (post.raised_amount || 0) + (post.simulated_raised_amount || 0);
        if (fresh && freshVisual !== currentVisual) {
          setPost(fresh);
          // Pulse effect on the raised amount for 1.5 s
          setJustUpdated(true);
          setTimeout(() => setJustUpdated(false), 1500);
        }
      } catch {
        // Network errors are silent — next tick will retry
      }
    };

    timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id, post.raised_amount, post.goal_amount]);

  const raised = post.raised_amount || 0;
  const goal = post.goal_amount || 1;
  // Total visual = real + simulado (el simulado es solo para FOMO, nunca toca pagos ni carteras)
  const simulated = post.simulated_raised_amount || 0;
  const visualRaised = raised + simulated;
  const pct = Math.min(Math.round((visualRaised / goal) * 100), 100);
  // Recaudación cerrada: hard_cap activado Y objetivo REAL alcanzado (no visual)
  const isClosed = !!(post.hard_cap && raised >= goal);
  // Contador visual de aportantes
  const realContributors = post.contributors_count || 0;
  const simContributors = post.simulated_contributors_count || 0;
  const visualContributors = realContributors + simContributors;

  // Visual tier availability uses real + simulated sold units
  const availableTiers = (post.tiers || []).filter(
    (t) => !t.max_units || ((t.sold_units || 0) + (t.simulated_sold_units || 0)) < t.max_units
  );
  const exhaustedTiers = (post.tiers || []).filter(
    (t) => t.max_units && ((t.sold_units || 0) + (t.simulated_sold_units || 0)) >= t.max_units
  );

  const isFree = selectedTierId === "free";
  const selectedTier = post.tiers?.find((t) => t.id === selectedTierId);
  const amount = selectedTier
    ? selectedTier.amount
    : isFree
    ? parseFloat(String(freeAmount).replace(",", ".")) || 0
    : 0;

  const handleAportar = async () => {
    if (isLocked) {
      toast.error("El post todavía no ha comenzado");
      return;
    }
    if (!authUser) {
      toast.error("Inicia sesión para contribuir");
      return;
    }
    if (!selectedTierId) {
      toast.error("Elige una recompensa o propina libre");
      return;
    }
    if (isFree && (isNaN(amount) || amount < 3.5 || amount > 500)) {
      toast.error("Introduce un importe entre 3,50€ y 500€");
      return;
    }

    setLoading(true);
    try {
      const body = {
        tier_id: isFree ? null : selectedTierId,
        amount,
        origin_url: window.location.origin,
        buyer_slug: authUser.slug || "",
      };
      const res = await axios.post(
        `${API}/fundraising/posts/${post.id}/initiate-stripe`,
        body,
        { withCredentials: true }
      );
      window.location.href = res.data.url;
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al iniciar el pago";
      if (err.response?.status === 409) {
        toast.error("Esta recompensa ya está agotada, elige otra");
      } else {
        toast.error(msg);
      }
      setLoading(false);
    }
  };

  return (
    <article
      className="rounded-2xl overflow-hidden"
      style={{ background: T.card, border: `1px solid ${T.line}` }}
      data-testid={`fundraising-card-${post.id}`}
    >
      {/* Creator header */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <Avatar name={creatorName} photoUrl={creatorAvatar} size={40} />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate" style={{ color: T.text }}>{creatorName}</p>
          {creatorSlug && (
            <p className="text-xs truncate" style={{ color: T.sub }}>@{creatorSlug}</p>
          )}
        </div>
        <span className="text-xs flex-shrink-0" style={{ color: T.sub2 }}>
          {timeAgo(post.created_at)}
        </span>
      </div>

      {/* Photo — full image, no crop, natural aspect ratio */}
      {post.photo_url && (
        <div className="overflow-hidden" style={{ background: "#000" }}>
          <img
            src={post.photo_url}
            alt=""
            className="w-full block"
            style={{ objectFit: "contain", maxHeight: 480 }}
            onError={(e) => { e.target.parentNode.style.display = "none"; }}
          />
        </div>
      )}

      {/* Caption */}
      {post.caption && (
        <p className="px-4 pt-3 text-sm leading-relaxed" style={{ color: T.text }}>
          {post.caption}
        </p>
      )}

      {/* Fundraising box */}
      <div className="mx-4 mt-3 mb-4 rounded-2xl p-4 space-y-3"
        style={{ background: T.card2, border: `1px solid ${T.line}` }}>
        {/* Eyebrow */}
        <p className="text-[10px] font-bold uppercase tracking-[0.12em]"
          style={{ color: T.pink, fontFamily: "'Barlow Condensed', sans-serif" }}>
          {isLocked ? "Próximamente · Objetivo en curso" : "Objetivo en curso"}
        </p>        {/* Amounts */}
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className="font-bold leading-none"
            data-testid="fr-raised-amount"
            style={{
              color: T.text,
              fontSize: 28,
              fontFamily: "'Barlow Condensed', sans-serif",
              transition: "color 0.4s, transform 0.4s",
              transform: justUpdated ? "scale(1.06)" : "scale(1)",
              display: "inline-block",
              color: justUpdated ? T.amber : T.text,
            }}>
            {fmt(visualRaised)}
          </span>
          <span className="text-sm" style={{ color: T.sub }}>
            de {fmt(goal)}
          </span>
          <span className="text-sm font-bold ml-auto flex-shrink-0"
            style={{ color: pct >= 100 ? T.amber : T.pink }}>
            {pct}%
          </span>
        </div>

        {/* Progress bar */}
        <ProgressBar raised={visualRaised} goal={goal} tiers={post.tiers || []} />

        {/* Contributor count — only shown when > 0 AND not hidden by creator */}
        {visualContributors > 0 && !post.hide_contributors_count && (
          <p className="text-xs" style={{ color: T.sub }} data-testid="fr-contributors-count">
            <span style={{ color: T.text, fontWeight: 600 }}>{visualContributors}</span>
            {" "}
            {visualContributors === 1 ? "persona ha aportado" : "personas han aportado"}
          </p>
        )}

        {/* Banner de cuenta atrás — solo visible para fans cuando está bloqueado */}
        {isLocked && (
          <CountdownBanner countdown={countdown} unlockAt={post.unlock_at} />
        )}

        {/* Tier list — each tile is clickable to select */}
        {post.tiers?.length > 0 && (
          <div className="space-y-2 pt-1">
            {post.tiers.map((tier) => {
              const visualSold = (tier.sold_units || 0) + (tier.simulated_sold_units || 0);
              const isExhausted = !!(tier.max_units && visualSold >= tier.max_units);
              const isUnlocked = visualRaised >= tier.amount;
              const isSelected = selectedTierId === tier.id;
              const remaining = tier.max_units ? tier.max_units - visualSold : null;
              return (
                <button
                  key={tier.id}
                  type="button"
                  disabled={isExhausted}
                  onClick={() => {
                    if (isLocked) { toast.error("El post todavía no ha comenzado"); return; }
                    if (!isExhausted) setSelectedTierId(tier.id);
                  }}
                  data-testid={`fr-tier-${tier.id}`}
                  className="w-full text-left flex items-start gap-3 rounded-xl px-3 py-2.5 transition-all duration-150"
                  style={{
                    background: isSelected
                      ? "rgba(255,0,102,0.14)"
                      : isUnlocked
                      ? "rgba(255,0,102,0.05)"
                      : T.bg,
                    border: `1.5px solid ${
                      isLocked ? "rgba(255,0,102,0.3)"
                      : isSelected ? T.pink
                      : isUnlocked ? "rgba(255,0,102,0.35)"
                      : T.line
                    }`,
                    boxShadow: isSelected ? `0 0 10px rgba(255,0,102,0.3)` : "none",
                    opacity: isExhausted ? 0.45 : 1,
                    cursor: isExhausted ? "not-allowed" : isLocked ? "not-allowed" : "pointer",
                  }}>
                  {/* Circle indicator */}
                  <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 transition-all"
                    style={{
                      background: isLocked ? "rgba(255,0,102,0.15)"
                        : isSelected ? T.pink
                        : isUnlocked ? "rgba(255,0,102,0.3)"
                        : "transparent",
                      border: `1.5px solid ${isLocked ? "rgba(255,0,102,0.4)" : isSelected ? T.pink : isUnlocked ? T.pink : T.line}`,
                    }}>
                    {isLocked
                      ? <Lock size={8} color="rgba(255,80,130,0.7)" />
                      : isSelected
                      ? <Check size={10} color="#fff" />
                      : isUnlocked && !isSelected
                      ? <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.pink }} />
                      : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-sm"
                        style={{ color: isLocked ? "rgba(255,80,130,0.85)" : isSelected ? T.pink : T.text, fontFamily: "'Barlow Condensed', sans-serif" }}>
                        {fmt(tier.amount)}
                      </span>
                      {/* Right side: countdown > exhausted > remaining */}
                      {isLocked ? (
                        <span
                          className="font-bold flex-shrink-0 flex items-center gap-1 text-[10px] uppercase tracking-wide"
                          style={{ color: "rgba(255,80,130,0.75)" }}
                          data-testid={`fr-tier-locked-${tier.id}`}
                        >
                          <Lock size={9} /> Bloqueado
                        </span>
                      ) : isExhausted ? (
                        <span className="text-[10px] font-bold uppercase tracking-wide flex-shrink-0 flex items-center gap-1"
                          style={{ color: T.sub2 }}>
                          <Lock size={9} /> Agotado
                        </span>
                      ) : remaining !== null ? (
                        <span className="text-[10px] flex-shrink-0"
                          style={{ color: isSelected ? T.pink : T.sub2 }}>
                          {remaining} de {tier.max_units} Disponibles
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs mt-0.5 leading-snug" style={{ color: isLocked ? "rgba(255,255,255,0.45)" : isSelected ? "rgba(255,255,255,0.75)" : T.sub }}>
                      {tier.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Selector */}
        {isClosed ? (
          /* ── Post cerrado — objetivo alcanzado con hard_cap ────────────── */
          <div className="rounded-xl px-4 py-5 text-center space-y-1.5"
            style={{ background: "rgba(255,184,0,0.08)", border: "1.5px solid rgba(255,184,0,0.35)" }}
            data-testid="fr-closed-banner"
          >
            <div className="flex items-center justify-center gap-2">
              <Lock size={14} style={{ color: T.amber }} />
              <span className="text-sm font-black uppercase tracking-wide" style={{ color: T.amber }}>
                Recaudación cerrada
              </span>
            </div>
            <p className="text-xs" style={{ color: T.sub }}>
              Se ha alcanzado el objetivo y la creadora ha cerrado las aportaciones.
            </p>
          </div>
        ) : (
        <div className="space-y-2 pt-1">
          <div className="relative">
            <select
              value={selectedTierId}
              onChange={(e) => {
                if (isLocked) { toast.error("El post todavía no ha comenzado"); return; }
                setSelectedTierId(e.target.value);
              }}
              disabled={isLocked}
              data-testid="fr-tier-selector"
              className="w-full rounded-xl px-4 py-2.5 text-sm appearance-none focus:outline-none"
              style={{
                background: T.bg,
                border: `1px solid ${T.line}`,
                color: selectedTierId ? T.text : T.sub,
                opacity: isLocked ? 0.5 : 1,
                cursor: isLocked ? "not-allowed" : "pointer",
              }}>
              <option value="" disabled>Elegir propina…</option>
              {availableTiers.map((t) => (
                <option key={t.id} value={t.id}>
                  {fmt(t.amount)} — {t.description.slice(0, 28)}{t.description.length > 28 ? "…" : ""}
                </option>
              ))}
              {exhaustedTiers.map((t) => (
                <option key={t.id} value={t.id} disabled>
                  {fmt(t.amount)} — {t.description.slice(0, 24)}… (agotado)
                </option>
              ))}
              <option value="free">Otra cantidad (sin recompensa fija)…</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: T.sub2 }} />
          </div>

          {/* Free amount input */}
          {isFree && (
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-sm"
                style={{ color: T.pink }}>€</span>
              <input
                type="number" min={3.5} max={500} step={0.01}
                value={freeAmount}
                onChange={(e) => setFreeAmount(e.target.value)}
                placeholder="Importe libre (3,50€ – 500€)"
                data-testid="fr-free-amount"
                className="w-full rounded-xl pl-8 pr-4 py-2.5 text-sm focus:outline-none"
                style={{
                  background: T.bg,
                  border: `1px solid ${T.line}`,
                  color: T.text,
                }}
              />
            </div>
          )}

          {/* Banner comisión especial */}
          {feeInfo.is_custom && selectedTierId && (
            <div
              data-testid="fr-commission-promo-banner"
              className="flex items-center gap-2 rounded-xl px-3 py-2"
              style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.3)" }}
            >
              <span style={{ fontSize: 14 }}>★</span>
              <div>
                <span className="text-xs font-bold" style={{ color: T.cyan }}>
                  ¡Comisión especial {feeInfo.pct}%!
                  {feeInfo.expires_at && (
                    <span className="font-normal opacity-70 ml-1">
                      · hasta {new Date(feeInfo.expires_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", timeZone: "Europe/Madrid" })}
                    </span>
                  )}
                </span>
                {feeCountdown && (
                  <p className="text-[10px] font-mono font-bold mt-0.5" style={{ color: T.cyan }}>
                    ⏱ {feeCountdown.d > 0 ? `${feeCountdown.d}d ${feeCountdown.h}h ${feeCountdown.m}m` : `${feeCountdown.h}h ${feeCountdown.m}m`} restantes
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Payment method toggle */}
          {paypalClientId && selectedTierId && (
            <div className="flex rounded-xl overflow-hidden"
              style={{ border: `1px solid ${T.line}` }}>
              {["stripe", "paypal"].map((m) => (
                <button key={m}
                  onClick={() => setPayMethod(m)}
                  className="flex-1 py-2 text-xs font-bold uppercase tracking-wide transition-colors"
                  style={{
                    background: payMethod === m ? T.pink : T.bg,
                    color: payMethod === m ? "#fff" : T.sub,
                    fontFamily: "'Barlow Condensed', sans-serif",
                  }}>
                  {m === "stripe" ? (
                    <><CreditCard size={12} className="inline mr-1" />Tarjeta</>
                  ) : (
                    <>PayPal</>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Stripe button */}
          {payMethod === "stripe" && (
            <button
              onClick={handleAportar}
              disabled={loading || isLocked || !selectedTierId || (isFree && !freeAmount)}
              data-testid="fr-aportar-btn"
              className="w-full rounded-xl py-3 font-bold uppercase tracking-wide text-sm transition-opacity disabled:opacity-40"
              style={{
                background: isLocked ? "rgba(255,0,102,0.3)" : T.pink,
                color: "#fff",
                fontFamily: "'Barlow Condensed', sans-serif",
                cursor: isLocked ? "not-allowed" : "pointer",
              }}>
              {loading ? "Redirigiendo…"
                : isLocked ? (
                  <span className="flex items-center justify-center gap-2">
                    <Lock size={14} />
                    Abre el {fmtUnlockDate(post.unlock_at).split(" a las")[0]}
                  </span>
                )
                : selectedTier ? `Aportar ${fmt(amount)}` : "Aportar"}
            </button>
          )}

          {/* PayPal buttons */}
          {payMethod === "paypal" && selectedTierId && paypalClientId && (
            <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "EUR" }}>
              <PayPalButtons
                style={{ layout: "horizontal", tagline: false, height: 40 }}
                createOrder={async () => {
                  const res = await axios.post(
                    `${API}/fundraising/posts/${post.id}/initiate-paypal`,
                    { tier_id: isFree ? null : selectedTierId, amount },
                    { withCredentials: true }
                  );
                  return res.data.order_id;
                }}
                onApprove={async (data) => {
                  setLoading(true);
                  try {
                    const res = await axios.post(
                      `${API}/fundraising/posts/${post.id}/capture-paypal/${data.orderID}`,
                      {},
                      { withCredentials: true }
                    );
                    toast.success("¡Aportación registrada con PayPal!");
                    // Refresh post data
                    const updated = await axios.get(`${API}/fundraising/posts/${post.id}`);
                    setPost(updated.data);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Error al procesar el pago");
                  } finally {
                    setLoading(false);
                  }
                }}
                onError={(err) => {
                  console.error("PayPal error", err);
                  toast.error("Error en PayPal. Inténtalo con tarjeta.");
                }}
              />
            </PayPalScriptProvider>
          )}
        </div>
        )} {/* end isClosed ? ... : (...) */}
      </div>
    </article>
  );
}
